import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
    }
  }, [token, navigate]);

  const isPasswordValid = () => {
    return (
      password.length >= 10 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) &&
      password === confirmPassword
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid()) {
      toast.error('Please ensure all password requirements are met');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      toast.success('Password reset successfully! You can now login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_action-portal/artifacts/0jx7uwn8_HLRA_logo_non_transparent.jpeg" 
              alt="HLRA Logo" 
              className="h-24 w-auto"
            />
          </div>
          <CardTitle className="text-3xl font-bold">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              Set New Password
            </span>
          </CardTitle>
          <CardDescription className="text-base">
            Choose a strong password for your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-new-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-xs space-y-1 bg-gray-50 p-3 rounded-lg">
              <p className="font-medium text-gray-700 mb-2">Password requirements:</p>
              <p className={password.length >= 10 ? 'text-green-600' : 'text-gray-500'}>
                {password.length >= 10 ? '✓' : '○'} At least 10 characters
              </p>
              <p className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[A-Z]/.test(password) ? '✓' : '○'} At least 1 uppercase letter (A-Z)
              </p>
              <p className={/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[a-z]/.test(password) ? '✓' : '○'} At least 1 lowercase letter (a-z)
              </p>
              <p className={/[0-9]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[0-9]/.test(password) ? '✓' : '○'} At least 1 number (0-9)
              </p>
              <p className={/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) ? 'text-green-600' : 'text-gray-500'}>
                {/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password) ? '✓' : '○'} At least 1 special character (!@#$% etc.)
              </p>
              <p className={password && confirmPassword && password === confirmPassword ? 'text-green-600' : 'text-gray-500'}>
                {password && confirmPassword && password === confirmPassword ? '✓' : '○'} Passwords match
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-medium"
              disabled={loading || !isPasswordValid()}
              data-testid="reset-password-submit"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <div className="text-center text-sm">
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
